/**
 * Lecture d'un fichier de notes — CSV ou XLSX — jusqu'à des lignes prêtes à envoyer.
 *
 * Tout ce qui décide est ici, et rien n'y touche à React : c'est ce qui rend le module
 * vérifiable hors navigateur, sur des fichiers réels. L'écran ne fait qu'afficher ce que ces
 * fonctions rendent.
 *
 * Le découpage suit les deux natures du travail :
 * - `lireFichier` dépend des bibliothèques et du navigateur (`File`, `TextDecoder`) ;
 * - `analyserLignes`, et tout ce qui la sert, est **pur** — des tableaux entrent, des lignes
 *   validées sortent. C'est la partie qui se trompe, donc celle qu'on teste.
 */

import { NOTE_TOPICS } from '../constants/noteTopics';

/** Colonnes du modèle, dans l'ordre où elles y figurent. */
export const COLONNES = ['contenu', 'date_debut', 'date_fin', 'sujet', 'afficher_sur_graphiques'];

/** Celles sans lesquelles il n'y a pas de note. */
export const COLONNES_REQUISES = ['contenu', 'date_debut'];

/** Au-delà, on refuse le fichier : le serveur applique le même plafond. */
export const LIGNES_MAX = 1000;

/**
 * Plafond de longueur d'une note importée.
 *
 * Le champ est un `text` Postgres et aucune route ne le borne : une cellule de quarante mille
 * caractères passerait et rendrait illisibles la liste, le calendrier et les graphiques. On
 * borne donc **à l'import seulement** — la saisie manuelle reste libre, personne n'y colle un
 * roman par accident.
 */
export const CONTENU_MAX = 2000;

/** Au-delà, on refuse avant même de lire : un fichier de cette taille figerait l'onglet. */
export const OCTETS_MAX = 5 * 1024 * 1024;

/**
 * Un en-tête ramené à sa forme canonique.
 *
 * « Date début », « DATE_DEBUT » et « date-debut » désignent la même colonne. Le nom reste
 * imposé — on ne reconnaît pas de synonymes — mais être pointilleux sur les accents et la
 * casse ferait échouer des fichiers corrects pour rien.
 */
export const normaliserEntete = (brut) => String(brut ?? '')
  // Une marque d'ordre d'octets survit en tête du premier en-tête selon le chemin de lecture.
  // Invisible, elle ferait échouer la comparaison sans qu'on voie pourquoi.
  .replace(/\uFEFF/g, '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .trim()
  .toLowerCase()
  .replace(/[\s\-.]+/g, '_')
  .replace(/^_+|_+$/g, '');

/** Libellé de sujet → clé de l'enum Prisma, insensible à la casse et aux accents. */
const SUJETS_PAR_LIBELLE = new Map();
for (const t of NOTE_TOPICS) {
  SUJETS_PAR_LIBELLE.set(normaliserEntete(t.label), t.value);
  SUJETS_PAR_LIBELLE.set(normaliserEntete(t.value), t.value);
}

/** Les libellés tels qu'on les propose dans le modèle et dans les messages d'erreur. */
export const LIBELLES_SUJETS = NOTE_TOPICS.map(t => t.label);

// Excel en français écrit VRAI/FAUX ; les exports d'outils anglophones, true/false.
const OUI = new Set(['oui', 'o', 'yes', 'y', 'true', 'vrai', '1', 'x']);
const NON = new Set(['non', 'n', 'no', 'false', 'faux', '0']);

/**
 * Bornes plausibles d'un numéro de série Excel, en jours depuis le 30/12/1899.
 *
 * Une cellule de date à format personnalisé — courant en France — n'est pas reconnue comme
 * date par le lecteur : elle arrive en nombre brut. On la convertit, mais seulement dans une
 * plage qui ne peut pas être autre chose qu'une date (1954-2064). Hors de là, un nombre dans
 * une colonne de date est une erreur, pas une date à deviner.
 */
const SERIAL_MIN = 20000;
const SERIAL_MAX = 60000;

/** Le 30 décembre 1899, origine des numéros de série d'Excel. */
const ORIGINE_EXCEL = Date.UTC(1899, 11, 30);

/**
 * Date d'une cellule, en `AAAA-MM-JJ`, ou `null` si la cellule ne dit pas une date.
 *
 * **Rien ne passe par un objet `Date` construit en heure locale.** `new Date(2025, 2, 14)`
 * suivi d'un `toISOString()` rend la veille à l'ouest de Greenwich ; les composantes d'un
 * `Date` reçu du lecteur XLSX se lisent donc en UTC, et le texte se convertit par expression
 * régulière, sans jamais fabriquer de `Date`.
 */
export const enDateIso = (cellule) => {
  if (cellule == null || cellule === '') return null;

  if (cellule instanceof Date) {
    if (Number.isNaN(cellule.getTime())) return null;
    return isoDepuisUtc(cellule);
  }

  // Numéro de série Excel : une date à format personnalisé arrive ainsi.
  if (typeof cellule === 'number') {
    if (!Number.isFinite(cellule) || cellule < SERIAL_MIN || cellule > SERIAL_MAX) return null;
    // La partie fractionnaire porte l'heure ; on tronque au jour, avec une seconde de marge
    // pour absorber les séries écrites 45000,9999999 qui valent en réalité le jour suivant.
    const jours = Math.floor(cellule + 1 / 86400);
    return isoDepuisUtc(new Date(ORIGINE_EXCEL + jours * 86400000));
  }

  const texte = String(cellule).trim();

  // `AAAA-MM-JJ`, la forme déjà normalisée.
  const iso = texte.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (iso) return estUneVraieDate(+iso[1], +iso[2], +iso[3]) ? texte : null;

  // `JJ/MM/AAAA`, la forme française. Jamais lue en `MM/JJ` : un fichier français qui dit
  // 03/04 dit le 3 avril, et deviner l'ordre à partir des valeurs se tromperait un jour sur
  // douze sans que rien ne le signale.
  const fr = texte.match(/^(\d{1,2})[/.](\d{1,2})[/.](\d{4})$/);
  if (fr) {
    const [j, m, a] = [+fr[1], +fr[2], +fr[3]];
    if (!estUneVraieDate(a, m, j)) return null;
    return `${a}-${String(m).padStart(2, '0')}-${String(j).padStart(2, '0')}`;
  }

  return null;
};

const isoDepuisUtc = (d) => {
  const mois = String(d.getUTCMonth() + 1).padStart(2, '0');
  const jour = String(d.getUTCDate()).padStart(2, '0');
  return `${d.getUTCFullYear()}-${mois}-${jour}`;
};

/** Le 31 février n'existe pas, et `new Date` le convertirait en 3 mars sans se plaindre. */
const estUneVraieDate = (a, m, j) => {
  if (m < 1 || m > 12 || j < 1) return false;
  const d = new Date(Date.UTC(a, m - 1, j));
  return d.getUTCFullYear() === a && d.getUTCMonth() === m - 1 && d.getUTCDate() === j;
};

/**
 * Les lignes du fichier, validées une à une.
 *
 * Rend **toutes** les lignes, valides ou non, chacune portant ses erreurs : l'écran doit
 * pouvoir montrer ce qui cloche à côté de ce qui passe. Une erreur nomme la colonne et la
 * valeur reçue — « date_debut : « 32/13/2025 » n'est pas une date » se corrige, « ligne
 * invalide » ne se corrige pas.
 *
 * @param {string[]} entetes en-têtes bruts du fichier
 * @param {Array<Array<*>>} lignes cellules, dans l'ordre des en-têtes
 * @returns {{erreurEntetes: string|null, lignes: Array, videsIgnorees: number}}
 */
export function analyserLignes(entetes, lignes) {
  const colonnes = (entetes || []).map(normaliserEntete);
  const manquantes = COLONNES_REQUISES.filter(c => !colonnes.includes(c));
  const pluriel = (n) => (n > 1 ? 's' : '');

  if (manquantes.length > 0) {
    return {
      erreurEntetes: `Colonne${pluriel(manquantes.length)} obligatoire${pluriel(manquantes.length)} `
        + `manquante${pluriel(manquantes.length)} : ${manquantes.join(', ')}. `
        + `Colonnes trouvées : ${colonnes.filter(Boolean).join(', ') || 'aucune'}.`,
      lignes: [],
      videsIgnorees: 0
    };
  }

  // Deux colonnes de même nom rendraient le choix arbitraire : on lirait la première et la
  // seconde disparaîtrait en silence. Mieux vaut le dire.
  const doublons = COLONNES.filter(c => colonnes.filter(x => x === c).length > 1);
  if (doublons.length > 0) {
    return {
      erreurEntetes: `Colonne${pluriel(doublons.length)} en double : ${doublons.join(', ')}. `
        + 'Chaque colonne ne peut apparaître qu\'une fois.',
      lignes: [],
      videsIgnorees: 0
    };
  }

  const cellule = (ligne, nom) => {
    const i = colonnes.indexOf(nom);
    return i === -1 ? '' : (ligne[i] ?? '');
  };

  const analysees = [];
  let videsIgnorees = 0;
  // Clé (contenu, date de début) déjà vue **dans ce fichier** : le serveur ne dédoublonne
  // rien, et deux lignes identiques créeraient deux notes.
  const vues = new Set();

  lignes.forEach((ligne, rang) => {
    // Une ligne entièrement vide est ignorée, mais comptée : Excel en laisse traîner en fin
    // de feuille, et les compter comme des erreurs ferait passer un fichier sain pour cassé.
    // Le compte est affiché, pour qu'un trou au milieu du fichier ne se perde pas non plus.
    if (ligne.every(v => v == null || String(v).trim() === '')) {
      videsIgnorees++;
      return;
    }

    const erreurs = [];
    const contenu = String(cellule(ligne, 'contenu') ?? '').trim();
    if (!contenu) erreurs.push('contenu : la note est vide');
    else if (contenu.length > CONTENU_MAX) {
      erreurs.push(`contenu : ${contenu.length} caractères, maximum ${CONTENU_MAX}`);
    }

    const brutDebut = cellule(ligne, 'date_debut');
    const debut = enDateIso(brutDebut);
    if (!debut) {
      erreurs.push(String(brutDebut).trim()
        ? `date_debut : « ${String(brutDebut).trim()} » n'est pas une date (JJ/MM/AAAA)`
        : 'date_debut : la date de début est obligatoire');
    }

    const brutFin = cellule(ligne, 'date_fin');
    let fin = null;
    if (String(brutFin ?? '').trim() !== '') {
      fin = enDateIso(brutFin);
      if (!fin) erreurs.push(`date_fin : « ${String(brutFin).trim()} » n'est pas une date (JJ/MM/AAAA)`);
      else if (debut && fin < debut) erreurs.push('date_fin : elle précède la date de début');
    }

    // Un sujet non reconnu est une erreur, pas un `null` : le laisser passer perdrait
    // l'information sans le dire, et rangerait la note parmi celles qui restent à classer.
    const brutSujet = String(cellule(ligne, 'sujet') ?? '').trim();
    let sujet = null;
    if (brutSujet) {
      sujet = SUJETS_PAR_LIBELLE.get(normaliserEntete(brutSujet)) || null;
      if (!sujet) erreurs.push(`sujet : « ${brutSujet} » inconnu (${LIBELLES_SUJETS.join(', ')})`);
    }

    // Une cellule vide ne vaut pas « non » : elle vaut « ce que dit le bandeau ». D'où
    // `undefined` et non `false` — l'écran comblera avec sa propre valeur.
    const brutGraphiques = String(cellule(ligne, 'afficher_sur_graphiques') ?? '').trim().toLowerCase();
    let surGraphique;
    if (brutGraphiques) {
      if (OUI.has(brutGraphiques)) surGraphique = true;
      else if (NON.has(brutGraphiques)) surGraphique = false;
      else erreurs.push(`afficher_sur_graphiques : « ${brutGraphiques} » n'est ni oui ni non`);
    }

    const cle = `${contenu}|${debut || ''}`;
    const doublonInterne = erreurs.length === 0 && vues.has(cle);
    if (erreurs.length === 0) vues.add(cle);

    analysees.push({
      // Le numéro est celui du tableur, en-tête comprise : c'est celui que l'utilisateur
      // lit dans Excel quand il va corriger. Un index de tableau ne lui dirait rien.
      numero: rang + 2,
      contenu,
      debut,
      fin,
      sujet,
      surGraphique,
      erreurs,
      doublonInterne
    });
  });

  return { erreurEntetes: null, lignes: analysees, videsIgnorees };
}

/**
 * Le contenu du modèle à télécharger.
 *
 * Séparateur `;` et BOM en tête : sans le premier, Excel en locale française met toute la
 * ligne dans la première colonne ; sans le second, il lit l'UTF-8 comme du Latin-1 et les
 * accents deviennent illisibles. Les deux sont nécessaires, et aucun ne se voit à l'œil.
 *
 * Les exemples restent des lignes ordinaires, et leur contenu le dit : oubliés dans le
 * fichier, ils s'importeraient comme de vraies notes. Aucune convention de commentaire ne les
 * en protège — un `#` traité comme tel ferait disparaître en silence une vraie note qui
 * commencerait par ce caractère. C'est l'aperçu qui protège : les trois lignes s'y voient,
 * nommées « exemple », et se décochent d'un clic.
 */
export function modeleCsv() {
  const exemples = [
    ['EXEMPLE à supprimer — soldes d\'hiver', '15/01/2026', '31/01/2026', 'Opération commerciale', 'oui'],
    ['EXEMPLE à supprimer — panne du tunnel de paiement', '03/02/2026', '', 'Bug', 'oui'],
    ['EXEMPLE à supprimer — refonte de la page d\'accueil', '10/02/2026', '', '', 'non']
  ];
  return '\uFEFF' + [COLONNES, ...exemples].map(l => l.map(echapper).join(';')).join('\r\n') + '\r\n';
}

/** Une valeur CSV : mise entre guillemets dès qu'elle contient un séparateur ou un guillemet. */
const echapper = (valeur) => {
  const texte = String(valeur ?? '');
  return /[";\r\n]/.test(texte) ? `"${texte.replace(/"/g, '""')}"` : texte;
};

/** Extensions acceptées, pour l'attribut `accept` comme pour le contrôle après dépôt. */
export const EXTENSIONS = ['.csv', '.xlsx'];

/**
 * Lit un fichier déposé et rend ses feuilles.
 *
 * Rend toujours un tableau `[{ nom, entetes, lignes }]` : un CSV n'en a qu'une, un classeur
 * peut en avoir plusieurs, et c'est à l'écran de faire choisir plutôt qu'au module de
 * décider en silence — les gens rangent volontiers leurs données sur la deuxième feuille.
 *
 * Les deux bibliothèques sont chargées **à la demande, dans leur branche** : cet écran est
 * rare, et le lecteur XLSX ne doit pas être téléchargé pour lire un CSV.
 */
export async function lireFichier(fichier) {
  const nom = (fichier?.name || '').toLowerCase();

  if (fichier.size > OCTETS_MAX) {
    throw new Error(`Fichier trop volumineux (${Math.round(fichier.size / 1024 / 1024)} Mo, maximum ${OCTETS_MAX / 1024 / 1024} Mo).`);
  }

  if (nom.endsWith('.xlsx')) return lireXlsx(fichier);
  if (nom.endsWith('.csv')) return lireCsv(fichier);

  throw new Error(`Format non reconnu. Formats acceptés : ${EXTENSIONS.join(', ')}.`);
}

/**
 * Toutes les feuilles d'un classeur.
 *
 * L'export par défaut est bien celui qui rend le classeur entier — `readSheet`, l'export
 * nommé, n'en rendrait qu'une, et laisserait perdre en silence les données rangées ailleurs.
 */
async function lireXlsx(fichier) {
  const module = await import('read-excel-file/browser');
  const lireClasseur = module.default;

  let feuilles;
  try {
    feuilles = await lireClasseur(fichier);
  } catch (e) {
    throw new Error('Le classeur n\'a pas pu être lu. Vérifiez qu\'il s\'agit bien d\'un .xlsx non protégé par mot de passe.');
  }

  const utiles = (feuilles || [])
    .map(f => ({ nom: f.sheet, grille: f.data || [] }))
    .filter(f => f.grille.length > 1);

  if (utiles.length === 0) throw new Error('Le classeur ne contient aucune feuille avec des données.');

  return utiles.map(f => ({ nom: f.nom, entetes: f.grille[0], lignes: f.grille.slice(1) }));
}

async function lireCsv(fichier) {
  const { default: Papa } = await import('papaparse');
  const texte = await texteDuFichier(fichier);

  // Excel écrit parfois `sep=;` en première ligne pour annoncer son séparateur. Papa Parse
  // ne connaît pas cette convention : la ligne deviendrait l'en-tête et tout le fichier
  // serait illisible.
  const annonce = texte.match(/^sep=(.)\r?\n/i);
  const corps = annonce ? texte.slice(annonce[0].length) : texte;

  const resultat = Papa.parse(corps, {
    // Excel exporte avec `;` en locale française, `,` ailleurs, et parfois une tabulation.
    // Papa Parse tranche sur le contenu ; imposer la virgule casserait un fichier sur deux.
    ...(annonce ? { delimiter: annonce[1] } : { delimitersToGuess: [';', ',', '\t', '|'] }),
    skipEmptyLines: 'greedy',
    dynamicTyping: false,
    // `header: false` : on veut les en-têtes bruts pour les normaliser nous-mêmes et pouvoir
    // dire lesquels ont été trouvés quand il en manque un.
    header: false
  });

  const grille = resultat.data || [];
  if (grille.length === 0) throw new Error('Le fichier est vide.');

  return [{ nom: null, entetes: grille[0], lignes: grille.slice(1) }];
}

/**
 * Le texte d'un fichier CSV, en devinant son encodage.
 *
 * Excel sait encore exporter en Windows-1252. `TextDecoder` en mode strict lève sur toute
 * séquence UTF-8 invalide : c'est un signal net, là où chercher des caractères de
 * remplacement après coup revient à deviner.
 */
async function texteDuFichier(fichier) {
  const octets = await fichier.arrayBuffer();
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(octets);
  } catch (e) {
    return new TextDecoder('windows-1252').decode(octets);
  }
}
